import React, { PropsWithChildren, ReactNode, useRef } from 'react';
import { Divider } from '../divider';
import { BaseText } from '../text';
import { Stack } from '../stack';
import { TextButton } from '../button';
import { semantic_colors } from '../../foundation';
import { ModalCancelReason } from './Modal';
import { ModalOverlay, ModalOverlayBaseProps } from './ModalOverlay';
import { FloatingTreeContext } from '../context';
import { Checkbox } from '../checkbox';
import { useProviderConfig } from '../provider';
import { CSSValueWithLength, getZIndex } from '../../styles';

export interface NoticeModalProps extends Omit<ModalOverlayBaseProps, 'fill' | 'width' | 'onClose'> {
  /**
   * 헤더 영역 상단 라벨 노출 요소
   */
  label?: string;
  title?: ReactNode;
  description?: ReactNode;
  /**
   * Modal 너비를 지정할 수 있습니다.
   * @default 500
   */
  width?: CSSValueWithLength;
  /** @default false */
  checked?: boolean;
  /**
   * 푸터 영역 좌측 체크박스 라벨 텍스트
   * @default '오늘 하루동안 이 창을 열지 않음'
   */
  checkboxLabel?: string;
  /**
   * reason을 포함한 cancel 이벤트를 전달합니다.
   *  - 'cancelClick' | 'escapeKeyPress' | 'overlayClick'
   */
  onCancel?: (reason: ModalCancelReason) => void;
  /**
   * 푸터 영역 좌측 체크박스 change 이벤트를 전달합니다.
   */
  onCheckedChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const NoticeModalInner = ({
  open: openProp,
  children,
  width = 500,
  zIndex = getZIndex('modal'),
  label,
  title,
  description,
  checked = false,
  checkboxLabel,
  onExited,
  onCancel,
  onCheckedChange,
  ...props
}: PropsWithChildren<NoticeModalProps>) => {
  const { locale } = useProviderConfig();
  const checkbox = useRef<HTMLInputElement | null>(null);

  const handleChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    checkbox.current?.blur();
    onCheckedChange?.(e);
  };

  return (
    <ModalOverlay
      width={width}
      borderRadiusSize='small'
      open={openProp}
      onExited={onExited}
      onCancelEventCallback={(event) => onCancel?.(event as ModalCancelReason)}
      zIndex={zIndex}
      controlledOpen
      {...props}
    >
      {(title || label || description) && (
        <Stack direction='column' p='20px 30px' gap={10}>
          {label && (
            <BaseText typography='Body_13_Bold' color={semantic_colors.brand.primary}>
              {label}
            </BaseText>
          )}
          {title && (
            <BaseText typography='Heading_24_Bold' color={semantic_colors.content.primary}>
              {title}
            </BaseText>
          )}
          {description && (
            <BaseText typography='Body_14_Regular' color={semantic_colors.content.secondary}>
              {description}
            </BaseText>
          )}
        </Stack>
      )}
      {children}
      <Stack direction='column' p='0 30px'>
        <Divider />
        <Stack direction='row' align='center' p='20px 0' justify='space-between'>
          <Checkbox ref={checkbox} checked={checked} onChange={handleChangeCheckbox}>
            {checkboxLabel ?? locale?.NoticeModal.checkReplayText}
          </Checkbox>
          <TextButton onClick={() => onCancel?.('cancelClick')}>{locale?.NoticeModal.closeText}</TextButton>
        </Stack>
      </Stack>
    </ModalOverlay>
  );
};

const NoticeModalWithContext = FloatingTreeContext(NoticeModalInner);
export const NoticeModal = (props: PropsWithChildren<NoticeModalProps>) => <NoticeModalWithContext {...props} />;
